import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { AlertCircle, ChevronRight, RefreshCw, Search, X } from "lucide-react";
import * as React from "react";

import { useCapabilityManifest } from "@/features/capabilities";
import {
  type CatalogEntry,
  GROUP_ORDER,
  connectorsListKey,
  groupLabel,
  listConnectionsForCapability,
  usePollConnectionStatus,
  visibleConnectorCatalog,
} from "@/features/connectors";
import { useConnectorEntitlements } from "@/features/connectors/hooks/useConnectorEntitlements";
import {
  CapabilityBadge,
  CapabilityRow,
  ConnectorStatus,
  GroupHeader,
  IntegrationRowSkeleton,
  StatusChip,
} from "@/features/connectors/components";
import { ConnectorsIntro, ConnectorsIntroToggle } from "@/features/onboarding";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import {
  ConsentExpiryBanner,
  SyncInProgressChip,
  cn,
  consentExpiryUrgency,
  localize,
  toast,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

/**
 * Integrations index — Ando V2, May 24 2026 SOTA + DRY pass.
 *
 * Layout (top→bottom):
 *
 *   1. Sticky search row (cmd/ctrl-K focus, X clear, ``aria-live`` count)
 *   2. Oldest needs_reauth alert — surfaced when at least one connected
 *      capability has a stale token
 *   3. Section "Collegate" — capability rows with vendor brand SVG +
 *      health dot + last-sync + Gestisci CTA
 *   4. Section "Errori di caricamento" — capabilities whose
 *      ``listCapabilityConnections`` errored, with a Riprova action
 *   5. Per-domain sections "Da connettere" — fixed order via ``GROUP_ORDER``
 *   6. Cold-load skeleton / empty-state placeholders
 *
 * All row chrome flows through the shared ``<CapabilityRow>`` primitive
 * (3 tones: default / success / warning) — no parallel JSX shells.
 *
 * BE round-trips:
 *   ``listCapabilityConnections(slug)`` per catalog entry, fanned out
 *   via ``useQueries``. Errors surface as a dedicated section with a
 *   Riprova button (no more silently-swallowed 0 connessioni). After
 *   OAuth redirect the query cache is invalidated on mount when
 *   ``?status=connected`` is present so the user sees the freshly-linked
 *   row immediately.
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": rows address
 * capability slugs; vendor names only appear inside the per-capability
 * wizard. PEC is the documented exception (``aruba_pec`` / ``infocert_pec``).
 */
export const Route = createFileRoute("/_authed/_modal/integrations/")({
  component: IntegrationsIndex,
});

/** Filter-chip family dot per group ("terre & gioielli" wayfinding, low salience). */
const GROUP_DOT: Readonly<Record<CatalogEntry["group"], string>> = {
  productivity: "bg-family-comms",
  knowledge: "bg-family-knowledge",
  finance: "bg-family-business",
  compliance: "bg-family-automation",
  infrastructure: "bg-ink-4",
};

function IntegrationsIndex() {
  const { i18n } = useLingui();
  const manifest = useCapabilityManifest();
  const entitlements = useConnectorEntitlements();
  const entitledConnectorCatalog = React.useMemo(
    () => entitlements.filterCatalog(visibleConnectorCatalog),
    [entitlements.filterCatalog],
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion();
  const search = useSearch({ from: "/_authed/_modal/integrations/" });
  const [searchValue, setSearchValue] = React.useState("");
  // A hub deep-link can pre-select a category via ``?group=`` (see
  // ``useGoToIntegrations``); fall back to "all" on an absent/unknown value.
  const groupParam = (search as { group?: string }).group;
  const [activeGroup, setActiveGroup] = React.useState<CatalogEntry["group"] | "all">(
    groupParam && (GROUP_ORDER as readonly string[]).includes(groupParam)
      ? (groupParam as CatalogEntry["group"])
      : "all",
  );
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // ── Cmd/Ctrl-K focus shortcut ─────────────────────────────────────────
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── OAuth-callback handoff — polled, not optimistic ──────────────────
  // After ``redirect_url`` lands the user back on this hub, the BFF tacks
  // ``?status=connected&provider=<slug>`` onto the URL. The naive pattern
  // (toast immediately) lies to the user: PSD2 webhooks can take 1-2 min,
  // Cassetto AdE delega 24-48h. We instead derive the capability from the
  // vendor slug + start ``usePollConnectionStatus`` on that capability.
  // While pending, ``<SyncInProgressChip>`` sits on the matching row.
  // On ready ⇒ success toast. On timeout ⇒ softer "more time needed" toast.
  // The URL params are cleared the moment polling starts so a refresh
  // doesn't re-fire the flow.
  const statusParam = (search as { status?: string }).status;
  const providerParam = (search as { provider?: string }).provider;
  const accessModeParam = (search as { access_mode?: string }).access_mode;
  const connectionIdParam = (search as { connection_id?: string }).connection_id;
  const [pendingFlow, setPendingFlow] = React.useState<{
    capability: string;
    label: string;
    sendOnlyConnectionId?: string;
  } | null>(null);

  // Vendor slug → capability lookup via the catalog (white-label safe:
  // the capability is what we surface, the vendor stays a BE concern).
  const capabilityForVendor = React.useCallback(
    (vendor: string | undefined): { capability: string; label: string } | null => {
      if (!vendor) return null;
      for (const entry of entitledConnectorCatalog) {
        const match = entry.spec.vendors?.some((v) => v.id === vendor);
        if (match) {
          return { capability: entry.spec.capability, label: localize(entry.spec.title, i18n) };
        }
      }
      return null;
    },
    [entitledConnectorCatalog, i18n],
  );

  React.useEffect(() => {
    if (statusParam !== "connected") return;
    const flow = capabilityForVendor(providerParam);
    const narrow = accessModeParam === "send_only";
    const id = connectionIdParam;
    if (narrow && (providerParam !== "gmail" || !id || !/^[0-9a-f-]{36}$/i.test(id))) {
      toast.error("Impossibile verificare la connessione Solo invio. Riconnetti l’account.");
    } else if (flow) setPendingFlow(narrow ? { ...flow, sendOnlyConnectionId: id } : flow);
    // Strip the params immediately — refresh shouldn't replay the flow.
    //
    // TanStack Router + a ``passthrough`` Zod schema treats
    // ``{ ...prev, status: undefined }`` as "preserve key, drop value"
    // and re-serialises the URL with ``?status=`` (empty), leaving the
    // user stuck at ``/integrations?status=connected`` after a
    // successful OAuth (June 9, 2026 incident). Destructure-and-rest
    // strips the keys for real.
    void navigate({
      to: "/integrations",
      search: (prev) => {
        const { status: _s, provider: _p, error: _e, access_mode: _m, connection_id: _c, ...rest } = prev as Record<string, unknown>;
        return rest;
      },
      replace: true,
    });
  }, [statusParam, providerParam, accessModeParam, connectionIdParam, capabilityForVendor, navigate]);

  const poll = usePollConnectionStatus({
    capability: pendingFlow?.capability ?? null,
    enabled: pendingFlow !== null,
    sendOnlyConnectionId: pendingFlow?.sendOnlyConnectionId,
  });

  React.useEffect(() => {
    if (poll.status === "ready" && pendingFlow) {
      toast.success(i18n._(msg`${pendingFlow.label} collegata`), {
        description: pendingFlow.sendOnlyConnectionId ? "Connessione Solo invio attiva. Nessuna lettura o sincronizzazione della posta." : i18n._(msg`La connessione è attiva — Ando può iniziare a leggere.`),
      });
      void queryClient.invalidateQueries({ queryKey: connectorsListKey() });
      setPendingFlow(null);
    } else if (poll.status === "timeout" && pendingFlow) {
      toast.info(i18n._(msg`${pendingFlow.label} — sincronizzazione più lenta del previsto`), {
        description: i18n._(
          msg`Stiamo ancora aspettando il provider. Riprova fra 1-2 minuti — niente di rotto.`,
        ),
      });
      setPendingFlow(null);
    } else if (poll.status === "error" && pendingFlow) {
      toast.error(i18n._(msg`${pendingFlow.label} — errore di sincronizzazione`), {
        description: i18n._(msg`Riprova fra qualche minuto o riconnetti l'account.`),
      });
      setPendingFlow(null);
    }
  }, [poll.status, pendingFlow, queryClient, i18n]);

  // ── Per-capability connection fan-out (errors surfaced, not swallowed) ──
  const connectionQueries = useQueries({
    queries: entitledConnectorCatalog.map(({ spec }) => ({
      queryKey: connectorsListKey(spec.capability),
      // Source-aware: enterprise capabilities (database/erp/spreadsheet)
      // route to ``/enterprise/connectors`` here — the unified route 400s
      // on them, which used to surface as a FALSE "Errori di caricamento".
      queryFn: () => listConnectionsForCapability(spec.capability),
      staleTime: 60_000,
      retry: 1,
    })),
  });

  // ── Summaries — numeric date math, null sinks via Infinity ────────────
  const summaries = React.useMemo<SummaryRow[]>(() => {
    return entitledConnectorCatalog.map((entry, idx) => {
      const result = connectionQueries[idx];
      const conns = result?.data ?? [];
      const isLoading = result?.isLoading ?? false;
      const isError = !!result?.error;
      const lastUsedMs = conns
        .map((c) => (c.last_used_at ? Date.parse(c.last_used_at) : 0))
        .reduce((max, t) => (t > max ? t : max), 0);
      const needsReauth = conns.some((c) => c.token_status === "needs_reauth");
      // Enterprise connectors surface their probe health via
      // ``metadata.status`` (healthy/degraded/unhealthy/unknown); anything
      // worse than healthy paints a red dot + warning tone.
      const degraded = conns.some((c) => {
        const s = c.metadata?.status;
        return s === "degraded" || s === "unhealthy";
      });
      const primary = conns[0];
      // Pick the earliest non-null REAL consent expiry across the rows
      // so the banner above warns about the consent that lapses first.
      //
      // June 4 2026 fix: previously this read ``c.expires_at`` — the
      // OAuth ACCESS TOKEN lifetime, which auto-refreshes every ~1h and
      // is meaningless to the end-user. That triggered "consenso scade
      // domani" banners on Gmail / Calendar / Slack / Outlook even
      // though those connectors literally never expire from the user's
      // perspective. The right field is ``consent_expires_at``, which
      // the BE populates ONLY for PSD2 Open Banking + AdE Cassetto
      // Fiscale (the two capabilities that issue a user-visible consent
      // with a hard deadline). Missing on every other connector →
      // ``soonestExpiry`` stays null → banner never appears for them.
      //
      // V1 had the same audit note on ``SalesforcePage.tsx``: "do NOT
      // use expires_at to mark a connection as broken — access tokens
      // auto-refresh on 401. Only the backend ``needs_reauth`` flag is
      // authoritative."
      const soonestExpiryMs = conns
        .map((c) =>
          c.consent_expires_at ? Date.parse(c.consent_expires_at) : Number.POSITIVE_INFINITY,
        )
        .reduce((min, t) => (t < min ? t : min), Number.POSITIVE_INFINITY);
      return {
        entry,
        isLoading,
        isError,
        connected: conns.length > 0,
        connectionCount: conns.length,
        lastUsedAt: lastUsedMs > 0 ? new Date(lastUsedMs).toISOString() : null,
        needsReauth,
        degraded,
        primaryLabel: primary?.connection_key ?? primary?.scope ?? null,
        primaryVendor: primary?.vendor ?? null,
        soonestExpiry: Number.isFinite(soonestExpiryMs)
          ? new Date(soonestExpiryMs).toISOString()
          : null,
      };
    });
  }, [connectionQueries, entitledConnectorCatalog]);

  // ── Search filter ─────────────────────────────────────────────────────
  const filteredSummaries = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (!needle) return summaries;
    return summaries.filter(({ entry }) => {
      const hay =
        `${entry.spec.title} ${entry.spec.subtitle ?? ""} ${entry.spec.capability}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [searchValue, summaries]);

  const connectedSummaries = filteredSummaries.filter((s) => s.connected);
  const disconnectedSummaries = filteredSummaries.filter((s) => !s.connected && !s.isError);
  const erroredSummaries = filteredSummaries.filter((s) => s.isError);

  // Oldest stale-token row wins (user wants to reconnect the most-broken one).
  const oldestStale = React.useMemo(() => {
    const stales = connectedSummaries.filter((s) => s.needsReauth);
    if (stales.length === 0) return null;
    return [...stales].sort((a, b) => {
      const ta = a.lastUsedAt ? Date.parse(a.lastUsedAt) : Number.POSITIVE_INFINITY;
      const tb = b.lastUsedAt ? Date.parse(b.lastUsedAt) : Number.POSITIVE_INFINITY;
      return ta - tb;
    })[0];
  }, [connectedSummaries]);

  // ── Cold-load detection — drives the skeleton ─────────────────────────
  const isColdLoading =
    entitlements.isPending || connectionQueries.every((q) => q.isLoading && !q.data);

  // ── Disconnected grouped by GROUP_ORDER (deterministic) ───────────────
  const groupedDisconnected = React.useMemo(() => {
    const buckets: Record<string, typeof disconnectedSummaries> = {};
    for (const s of disconnectedSummaries) {
      const g = s.entry.group;
      const bucket = buckets[g] ?? [];
      bucket.push(s);
      buckets[g] = bucket;
    }
    return buckets;
  }, [disconnectedSummaries]);

  // Per-group available counts (pre-search) — stable chip counts that don't
  // flicker while typing; the grid itself still reacts to search + filter.
  const groupCounts = React.useMemo(() => {
    const c: Partial<Record<CatalogEntry["group"], number>> = {};
    for (const s of summaries) {
      if (s.connected || s.isError) continue;
      c[s.entry.group] = (c[s.entry.group] ?? 0) + 1;
    }
    return c;
  }, [summaries]);
  const totalAvailable = GROUP_ORDER.reduce((sum, g) => sum + (groupCounts[g] ?? 0), 0);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-8">
      {/* Sticky toolbar band — counts + search + chips stay put while the list
          scrolls beneath (after the chips). ``-mx-8 px-8`` makes the cream fill
          span the full content width so rows scroll cleanly under it. */}
      <div className="-mx-8 sticky top-0 z-20 flex flex-col gap-3 bg-cream/95 px-8 pt-3 pb-3">
        {/* Compact meta row: live counts (left) + light "Come funzionano?" (right). */}
        <div className="flex items-center gap-3">
          <div className="text-[12px] text-ink-3">
            <Trans>
              <strong className="text-ink-1 tabular-nums">{connectedSummaries.length}</strong>{" "}
              collegate · <span className="tabular-nums">{disconnectedSummaries.length}</span>{" "}
              disponibili
            </Trans>
            {erroredSummaries.length > 0 ? (
              <>
                {" · "}
                <span className="font-medium text-warning tabular-nums">
                  <Trans>{erroredSummaries.length} con errore</Trans>
                </span>
              </>
            ) : null}
          </div>
          <ConnectorsIntroToggle />
        </div>
        {/* Search */}
        <div className="rounded-md border border-hairline bg-white/55 px-3 py-2">
          <label className="flex items-center gap-2">
            <Search size={14} strokeWidth={1.75} className="text-ink-3" aria-hidden />
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={i18n._(msg`Cerca: Gmail, fatturazione, banca…`)}
              role="searchbox"
              aria-label={i18n._(msg`Cerca integrazioni`)}
              aria-controls="integrations-results"
              className="flex-1 bg-transparent text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none"
            />
            {searchValue ? (
              <button
                type="button"
                onClick={() => {
                  setSearchValue("");
                  searchInputRef.current?.focus();
                }}
                aria-label={i18n._(msg`Cancella ricerca`)}
                className="grid h-5 w-5 place-items-center rounded text-ink-3 transition-colors hover:bg-black/[0.05] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
              >
                <X className="h-3 w-3" strokeWidth={2} aria-hidden />
              </button>
            ) : (
              <kbd className="rounded bg-black/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-ink-3">
                ⌘K
              </kbd>
            )}
          </label>
        </div>

        {/* Category filter chips — a client-side lens. Counts are pre-search
            per-group totals so they stay stable while the user types. */}
        <div
          className="flex flex-wrap gap-2"
          role="toolbar"
          aria-label={i18n._(msg`Filtra per categoria`)}
        >
          <FilterChip
            label={i18n._(msg`Tutte`)}
            count={totalAvailable}
            active={activeGroup === "all"}
            onClick={() => setActiveGroup("all")}
          />
          {GROUP_ORDER.map((g) =>
            (groupCounts[g] ?? 0) > 0 ? (
              <FilterChip
                key={g}
                label={groupLabel(g)}
                count={groupCounts[g] ?? 0}
                dot={GROUP_DOT[g]}
                active={activeGroup === g}
                onClick={() => setActiveGroup(g)}
              />
            ) : null,
          )}
        </div>
      </div>

      {/* Expandable "how connectors work" — inline, scrolls with content. */}
      <ConnectorsIntro />

      <div className="sr-only" aria-live="polite">
        {searchValue
          ? i18n._(msg`${filteredSummaries.length} integrazioni trovate per "${searchValue}".`)
          : ""}
      </div>

      {/* Oldest needs_reauth alert */}
      {oldestStale ? (
        <div className="flex items-center gap-3 rounded-xl border border-warning/30 bg-warning/[0.08] px-4 py-3">
          <AlertCircle size={16} strokeWidth={2} className="shrink-0 text-warning" aria-hidden />
          <div className="flex min-w-0 flex-1 flex-col text-sm">
            <span className="font-medium text-ink-1">
              {localize(oldestStale.entry.spec.title, i18n)}
            </span>
            <span className="text-ink-3 text-xs">
              <Trans>Token scaduto · richiede riconnessione</Trans>
            </span>
          </div>
          <Link
            to="/integrations/$capability"
            params={{ capability: oldestStale.entry.slug }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-warning/20 px-2.5 py-1 font-medium text-[11px] text-warning hover:bg-warning/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
          >
            <RefreshCw size={11} aria-hidden /> <Trans>Riconnetti</Trans>
          </Link>
        </div>
      ) : null}

      <div id="integrations-results">
        {/* Cold-load skeleton */}
        {isColdLoading ? (
          <section className="flex flex-col gap-2">
            <GroupHeader tone="infrastructure" loading />
            <ul className="flex flex-col gap-1.5" aria-busy>
              <IntegrationRowSkeleton />
              <IntegrationRowSkeleton />
              <IntegrationRowSkeleton />
            </ul>
          </section>
        ) : (
          <>
            {/* Section: Collegate */}
            {connectedSummaries.length > 0 ? (
              <section className="flex flex-col gap-2">
                <GroupHeader
                  tone="connected"
                  label={i18n._(msg`Collegate`)}
                  count={connectedSummaries.length}
                />
                <IntegrationsConsentBanners rows={connectedSummaries} />
                <ul className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {connectedSummaries.map((row, idx) => (
                    <motion.li
                      key={row.entry.slug}
                      initial={reducedMotion ? false : { opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: reducedMotion ? 0 : 0.18,
                        delay: reducedMotion ? 0 : Math.min(idx * 0.04, 0.2),
                        ease: "easeOut",
                      }}
                    >
                      <CapabilityRow
                        entry={row.entry}
                        primaryVendor={row.primaryVendor}
                        tone={row.needsReauth || row.degraded ? "warning" : "success"}
                        subtitle={
                          <>
                            <ConnectorStatus
                              variant="dot"
                              signals={{ needsReauth: row.needsReauth, degraded: row.degraded }}
                            />
                            <span className="truncate">
                              {row.connectionCount > 1
                                ? i18n._(msg`${row.connectionCount} connessioni`)
                                : (row.primaryLabel ?? i18n._(msg`collegato`))}
                            </span>
                            {row.lastUsedAt ? (
                              <>
                                <span aria-hidden>·</span>
                                <span className="shrink-0">
                                  <Trans>sync {formatRelativeTime(row.lastUsedAt)}</Trans>
                                </span>
                              </>
                            ) : null}
                            {row.needsReauth ? (
                              <>
                                <span aria-hidden>·</span>
                                <span className="shrink-0 font-medium text-warning">
                                  <Trans>token scaduto</Trans>
                                </span>
                              </>
                            ) : null}
                            {pendingFlow?.capability === row.entry.spec.capability &&
                            poll.status === "pending" ? (
                              <SyncInProgressChip
                                label={i18n._(msg`Sincronizzazione…`)}
                                etaSeconds={30}
                                className="ml-1"
                              />
                            ) : null}
                          </>
                        }
                        action={
                          <span className="font-medium text-[11px]">
                            {row.needsReauth ? i18n._(msg`Riconnetti`) : i18n._(msg`Gestisci`)}
                          </span>
                        }
                      />
                    </motion.li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Section: errored capabilities */}
            {erroredSummaries.length > 0 ? (
              <section className="mt-5 flex flex-col gap-2">
                <GroupHeader
                  tone="compliance"
                  label={i18n._(msg`Errori di caricamento`)}
                  count={erroredSummaries.length}
                />
                <ul className="flex flex-col gap-1.5">
                  {erroredSummaries.map(({ entry }) => (
                    <li key={entry.slug}>
                      <CapabilityRow
                        entry={entry}
                        mode="button"
                        tone="warning"
                        leading={<CapabilityBadge entry={entry} muted />}
                        subtitle={
                          <span className="flex items-center gap-1.5 text-warning">
                            <AlertCircle size={11} aria-hidden />
                            <Trans>Caricamento fallito — clicca per riprovare</Trans>
                          </span>
                        }
                        onClick={() =>
                          queryClient.invalidateQueries({
                            queryKey: connectorsListKey(entry.spec.capability),
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Per-group "Da connettere" — fixed order via GROUP_ORDER */}
            {GROUP_ORDER.map((group) => {
              if (activeGroup !== "all" && group !== activeGroup) return null;
              const list = groupedDisconnected[group];
              if (!list || list.length === 0) return null;
              return (
                <section key={group} className="mt-5 flex flex-col gap-2">
                  <GroupHeader tone={group} label={groupLabel(group)} count={list.length} />
                  <ul className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                    {list.map(({ entry }) => {
                      const diff = manifest.data?.capabilities?.[entry.spec.capability];
                      const gated = !!(diff && diff.state !== "satisfied");
                      return (
                        <li key={entry.slug}>
                          <Link
                            to="/integrations/$capability"
                            params={{ capability: entry.slug }}
                            className="group hover:-translate-y-px flex h-full flex-col gap-3 rounded-xl border border-hairline bg-card/60 p-4 shadow-sm transition duration-normal ease-smooth hover:bg-card/80 hover:shadow-md hover:ring-1 hover:ring-ando-orange-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <CapabilityBadge entry={entry} muted />
                              {gated ? <StatusChip variant="gated" /> : null}
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-ink-1 text-sm">
                                {localize(entry.spec.title, i18n)}
                              </span>
                              <p className="mt-0.5 line-clamp-2 text-[11px] text-ink-3">
                                {localize(entry.spec.subtitle, i18n)}
                              </p>
                            </div>
                            <span className="mt-auto flex items-center justify-end gap-1 font-medium text-[11px] text-ink-2 transition-colors group-hover:text-ando-orange">
                              <Trans>Collega</Trans>
                              <ChevronRight
                                size={13}
                                strokeWidth={2}
                                aria-hidden
                                className="transition-transform group-hover:translate-x-0.5"
                              />
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </>
        )}

        {/* Empty state if everything filtered out */}
        {!isColdLoading && filteredSummaries.length === 0 ? (
          <div className="mt-5 rounded-lg border border-hairline border-dashed p-6 text-center text-ink-3 text-sm">
            <Trans>Nessuna integrazione trovata per "{searchValue}".</Trans>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Local types ─────────────────────────────────────────────────────────

interface SummaryRow {
  readonly entry: CatalogEntry;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly connected: boolean;
  readonly connectionCount: number;
  readonly lastUsedAt: string | null;
  readonly needsReauth: boolean;
  /** Enterprise connector reporting ``degraded``/``unhealthy`` health. */
  readonly degraded: boolean;
  readonly primaryLabel: string | null;
  readonly primaryVendor: string | null;
  /** Earliest ``expires_at`` across the rows of this capability — drives
   * the shared ``<ConsentExpiryBanner>`` strip rendered above the
   * connected list. ``null`` when no row has a known expiry. */
  readonly soonestExpiry: string | null;
}

// ---------------------------------------------------------------------------
// IntegrationsConsentBanners — capability-level expiry strip. One banner per
// row whose soonest consent expires within 14 days (info → warning → critical
// at 14d / 7d / 2d). Silent on healthy rows so the section stays calm.
// Capability slug is rendered as a human label via the catalog spec title
// (already capability-only — no vendor leak).
// ---------------------------------------------------------------------------

interface IntegrationsConsentBannersProps {
  readonly rows: readonly SummaryRow[];
}

const IntegrationsConsentBanners: React.FC<IntegrationsConsentBannersProps> = ({ rows }) => {
  const { i18n } = useLingui();
  const expiring = rows.filter((r) => consentExpiryUrgency(r.soonestExpiry) !== null);
  if (expiring.length === 0) return null;
  return (
    <div className="mb-1 flex flex-col gap-2">
      {expiring.map((r) => (
        <ConsentExpiryBanner
          key={r.entry.slug}
          expiresAt={r.soonestExpiry}
          capabilityLabel={localize(r.entry.spec.title, i18n)}
        />
      ))}
    </div>
  );
};
IntegrationsConsentBanners.displayName = "IntegrationsConsentBanners";

// ---------------------------------------------------------------------------
// FilterChip — category lens pill for the toolbar. Local to the hub (same
// pattern as IntegrationsConsentBanners above). Active = orange outline + soft
// fill (the single accent); idle = hairline. Optional low-salience family dot.
// ---------------------------------------------------------------------------

interface FilterChipProps {
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly onClick: () => void;
  /** Tailwind ``bg-*`` for the leading family dot; omit for the "Tutte" chip. */
  readonly dot?: string;
}

const FilterChip: React.FC<FilterChipProps> = ({ label, count, active, onClick, dot }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-medium text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
      active
        ? "border-ando-orange bg-ando-orange-soft text-ink-1"
        : "border-hairline text-ink-2 hover:border-black/10 hover:bg-card/60",
    )}
  >
    {dot ? <span className={cn("h-1.5 w-1.5 rounded-full", dot)} aria-hidden /> : null}
    {label}
    <span className={cn("text-[11px] tabular-nums", active ? "text-ando-orange" : "text-ink-3")}>
      {count}
    </span>
  </button>
);
FilterChip.displayName = "FilterChip";
